/**
 * Channel Assessment — premium YouTube audit page.
 * Paste URL → real YouTube Data API fetch + Claude AI audit + actionable report.
 */
import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Youtube, Sparkles, AlertCircle, CheckCircle2, TrendingUp,
  Target, Users, Download, Loader2, Award, AlertTriangle, ArrowRight,
} from "lucide-react";
import PremiumNav from "@/components/premium/PremiumNav";
import PremiumFooter from "@/components/premium/PremiumFooter";
import { PremiumCard, PremiumButton, SectionHeading, Pill } from "@/components/premium/Primitives";
import AIProgressLoader from "@/components/premium/AIProgressLoader";
import ReportExportMenu from "@/components/premium/ReportExportMenu";

const API = process.env.REACT_APP_BACKEND_URL || '';

const SCORE_DEFS = [
  ["overall", "Overall"],
  ["seo", "SEO"],
  ["branding", "Branding"],
  ["thumbnails", "Thumbnails"],
  ["consistency", "Consistency"],
  ["growth", "Growth"],
  ["content_quality", "Content"],
  ["monetization_readiness", "Monetization"],
];

function scoreColor(v) {
  if (v == null) return "#94A3B8";
  if (v >= 75) return "#10B981";
  if (v >= 50) return "#A855F7";
  if (v >= 30) return "#F59E0B";
  return "#EF4444";
}

function ScoreDial({ label, value }) {
  const v = Math.max(0, Math.min(100, value ?? 0));
  const color = scoreColor(value);
  return (
    <div className="flex flex-col items-center p-4 rounded-xl bg-white border border-slate-200">
      <div className="relative w-16 h-16">
        <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
          <circle cx="18" cy="18" r="15.5" fill="none" stroke="#E2E8F0" strokeWidth="3"/>
          <circle cx="18" cy="18" r="15.5" fill="none" stroke={color} strokeWidth="3"
                  strokeDasharray={`${v * 0.975} 100`} strokeLinecap="round"/>
        </svg>
        <div className="absolute inset-0 flex items-center justify-center font-heading text-base font-bold text-[#111111]">
          {value ?? "—"}
        </div>
      </div>
      <div className="mt-2 text-xs text-slate-500 font-semibold text-center">{label}</div>
    </div>
  );
}

export default function PremiumChannelAssessment() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!url.trim()) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const r = await axios.post(`${API}/api/youtube/channel/assess`,
        { channel_url: url.trim() },
        { withCredentials: true });
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Audit failed");
    } finally { setLoading(false); }
  };

  const report = result?.report;

  const exportPDF = () => {
    if (!report) return;
    const stats = result?.has_real_data ? "LIVE DATA" : "AI ESTIMATE";
    const html = `
<!DOCTYPE html><html><head>
<meta charset="UTF-8"><title>Channel Audit — ${report.channel_name || "Report"}</title>
<style>
  @page { size: A4; margin: 18mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Inter', -apple-system, sans-serif; color: #111; margin: 0; line-height: 1.5; }
  h1 { font-family: 'Outfit', sans-serif; font-size: 32px; margin: 0 0 4px; letter-spacing: -0.02em; }
  h2 { font-family: 'Outfit', sans-serif; font-size: 20px; margin: 28px 0 12px; color: #A855F7; border-bottom: 2px solid #f0f0f0; padding-bottom: 6px; }
  .meta { color: #64748B; font-size: 13px; margin-bottom: 24px; }
  .brand { display: flex; align-items: center; justify-content: space-between; padding-bottom: 16px; border-bottom: 1px solid #e2e8f0; margin-bottom: 24px; }
  .brand-name { font-family: 'Outfit', sans-serif; font-size: 20px; font-weight: 700; }
  .brand-name .pp { color: #A855F7; }
  .pill { display: inline-block; padding: 4px 10px; border-radius: 99px; font-size: 11px; font-weight: 700; background: #f3e8ff; color: #7E22CE; text-transform: uppercase; letter-spacing: 0.05em; }
  .scores { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 12px; }
  .score { padding: 16px; background: #fafafa; border: 1px solid #e2e8f0; border-radius: 12px; text-align: center; }
  .score-num { font-family: 'Outfit', sans-serif; font-size: 28px; font-weight: 700; }
  .score-label { font-size: 11px; color: #64748B; text-transform: uppercase; margin-top: 4px; letter-spacing: 0.05em; }
  ul { padding-left: 18px; margin: 8px 0; }
  li { margin-bottom: 6px; font-size: 14px; }
  .action { padding: 12px 14px; background: #fafafa; border: 1px solid #e2e8f0; border-radius: 10px; margin-bottom: 8px; }
  .action-title { font-weight: 600; font-size: 14px; }
  .action-why { color: #64748B; font-size: 12px; margin-top: 4px; }
  .prio { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 700; margin-right: 8px; }
  .prio-high { background: #fee2e2; color: #b91c1c; }
  .prio-medium { background: #fef3c7; color: #92400e; }
  .prio-low { background: #e0e7ff; color: #4338ca; }
  .summary-block { padding: 14px 16px; background: #faf5ff; border-left: 3px solid #A855F7; border-radius: 8px; font-size: 14px; color: #4c1d95; margin: 12px 0; }
  .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e2e8f0; text-align: center; color: #94a3b8; font-size: 11px; }
  @media print { .no-print { display: none !important; } }
  .print-btn { position: fixed; top: 20px; right: 20px; background: #A855F7; color: white; padding: 10px 18px; border: 0; border-radius: 10px; font-weight: 600; cursor: pointer; }
</style></head><body>
<button class="print-btn no-print" onclick="window.print()">Save as PDF</button>
<div class="brand">
  <div class="brand-name">Markee<span class="pp">Mix</span></div>
  <span class="pill">${stats}</span>
</div>
<h1>${report.channel_name || "Channel Audit Report"}</h1>
<div class="meta">Generated ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })} · Powered by MarkeeMix Channel Assessment</div>

<div class="summary-block">${report.summary || ""}</div>

<h2>Performance scores</h2>
<div class="scores">
  ${SCORE_DEFS.map(([k, label]) => `<div class="score"><div class="score-num">${report.scores?.[k] ?? "—"}</div><div class="score-label">${label}</div></div>`).join("")}
</div>

<h2>Strengths</h2>
<ul>${(report.strengths || []).map((s) => `<li>${s}</li>`).join("")}</ul>

<h2>Weaknesses</h2>
<ul>${(report.weaknesses || []).map((s) => `<li>${s}</li>`).join("")}</ul>

<h2>Prioritized action plan</h2>
${(report.action_plan || []).map((a) => `<div class="action"><span class="prio prio-${a.priority || 'low'}">${(a.priority || "").toUpperCase()}</span><span class="action-title">${a.action}</span><div class="action-why">${a.why || ""}</div></div>`).join("")}

<h2>30-day growth plan</h2>
<ol>${(report.growth_30_day || []).map((s) => `<li>${s}</li>`).join("")}</ol>

<h2>90-day strategy</h2>
<ol>${(report.strategy_90_day || []).map((s) => `<li>${s}</li>`).join("")}</ol>

<h2>Content gaps to fill</h2>
<ul>${(report.content_gap || []).map((s) => `<li>${s}</li>`).join("")}</ul>

<h2>Competitor suggestions</h2>
<ul>${(report.competitors || []).map((s) => `<li>${s}</li>`).join("")}</ul>

<div class="footer">© ${new Date().getFullYear()} MarkeeMix Labs · markeemix.com · Channel Assessment Report</div>
<script>setTimeout(() => window.print(), 800);</script>
</body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); }
  };

  return (
    <div className="min-h-screen bg-white font-sans" data-testid="channel-assessment">
      <PremiumNav/>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <SectionHeading
            eyebrow={<><Youtube className="w-3 h-3 inline -mt-0.5 mr-1"/> YouTube Suite</>}
            title="Channel Assessment"
            subtitle="Paste any YouTube channel URL or @handle. Get a complete 25-metric audit with action plan in under 30 seconds."
          />
        </motion.div>

        {/* Input */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-8">
          <PremiumCard className="p-5" hover={false}>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && run()}
                placeholder="https://youtube.com/@channel  or  @handle  or  UC..."
                data-testid="ca-url-input"
                className="flex-1 px-4 py-3 rounded-xl border border-slate-200 focus:border-[#A855F7] focus:ring-4 focus:ring-brand-100 outline-none text-sm font-sans"
              />
              <PremiumButton
                onClick={run}
                loading={loading}
                icon={Sparkles}
                size="lg"
                data-testid="ca-run"
                disabled={!url.trim()}
              >
                {loading ? "Auditing…" : "Audit channel"}
              </PremiumButton>
            </div>
            {error && (
              <div className="mt-3 text-sm text-red-600 flex items-center gap-2" data-testid="ca-error">
                <AlertCircle className="w-4 h-4"/> {error}
              </div>
            )}
          </PremiumCard>
        </motion.div>

        {loading && (
          <div className="mt-8" data-testid="ca-loading">
            <AIProgressLoader
              visible={loading}
              label="auditing the channel"
              stages={[
                "fetching live channel data from YouTube",
                "analysing recent videos and engagement",
                "scoring SEO, thumbnails and consistency",
                "drafting your action plan",
              ]}
            />
          </div>
        )}

        {report && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-10 space-y-6" data-testid="ca-report">
            <PremiumCard className="p-6" hover={false}>
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  {report.channel_thumbnail && <img src={report.channel_thumbnail} alt="" className="w-14 h-14 rounded-full"/>}
                  <div>
                    <h2 className="font-heading text-2xl font-bold text-[#111111]">{report.channel_name || "Channel report"}</h2>
                    <p className="text-sm text-slate-500 mt-1">{report.summary}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Pill variant={result.has_real_data ? "brand" : "neutral"} icon={result.has_real_data ? CheckCircle2 : AlertTriangle}>
                    {result.has_real_data ? "LIVE DATA" : "AI ESTIMATE"}
                  </Pill>
                  {result.assessment_id && (
                    <ReportExportMenu type="channel" id={result.assessment_id} />
                  )}
                </div>
              </div>

              {/* Score dials */}
              <div className="mt-6 grid grid-cols-4 lg:grid-cols-8 gap-3" data-testid="ca-scores">
                {SCORE_DEFS.map(([k, label]) => (
                  <ScoreDial key={k} label={label} value={report.scores?.[k]} />
                ))}
              </div>
            </PremiumCard>

            {/* Strengths & weaknesses */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PremiumCard className="p-6" hover={false}>
                <div className="flex items-center gap-2 mb-4">
                  <Award className="w-4 h-4 text-emerald-600"/>
                  <h3 className="font-heading text-lg font-semibold text-[#111111]">Strengths</h3>
                </div>
                <ul className="space-y-2">
                  {(report.strengths || []).map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5"/> {s}
                    </li>
                  ))}
                </ul>
              </PremiumCard>
              <PremiumCard className="p-6" hover={false}>
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-4 h-4 text-amber-600"/>
                  <h3 className="font-heading text-lg font-semibold text-[#111111]">Weaknesses</h3>
                </div>
                <ul className="space-y-2">
                  {(report.weaknesses || []).map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5"/> {s}
                    </li>
                  ))}
                </ul>
              </PremiumCard>
            </div>

            {/* Action plan */}
            <PremiumCard className="p-6" hover={false}>
              <div className="flex items-center gap-2 mb-4">
                <Target className="w-4 h-4 text-[#A855F7]"/>
                <h3 className="font-heading text-lg font-semibold text-[#111111]">Prioritized action plan</h3>
              </div>
              <div className="space-y-3">
                {(report.action_plan || []).map((a, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl border border-slate-200">
                    <Pill variant={a.priority === "high" ? "brand" : a.priority === "medium" ? "outline" : "neutral"}>
                      {a.priority?.toUpperCase()}
                    </Pill>
                    <div>
                      <div className="text-sm font-semibold text-[#111111]">{a.action}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{a.why}</div>
                    </div>
                  </div>
                ))}
              </div>
            </PremiumCard>

            {/* Growth plans */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PremiumCard className="p-6" hover={false}>
                <h3 className="font-heading text-lg font-semibold text-[#111111] mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#A855F7]"/> 30-day growth plan
                </h3>
                <ol className="space-y-2 text-sm text-slate-700 list-decimal list-inside">
                  {(report.growth_30_day || []).map((line, i) => <li key={i}>{line}</li>)}
                </ol>
              </PremiumCard>
              <PremiumCard className="p-6" hover={false}>
                <h3 className="font-heading text-lg font-semibold text-[#111111] mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#A855F7]"/> 90-day strategy
                </h3>
                <ol className="space-y-2 text-sm text-slate-700 list-decimal list-inside">
                  {(report.strategy_90_day || []).map((line, i) => <li key={i}>{line}</li>)}
                </ol>
              </PremiumCard>
            </div>

            {/* Content gaps + competitors */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PremiumCard className="p-6" hover={false}>
                <h3 className="font-heading text-base font-semibold text-[#111111] mb-3">Content gaps to fill</h3>
                <div className="flex flex-wrap gap-2">
                  {(report.content_gap || []).map((c, i) => <Pill key={i} variant="brand">{c}</Pill>)}
                </div>
              </PremiumCard>
              <PremiumCard className="p-6" hover={false}>
                <h3 className="font-heading text-base font-semibold text-[#111111] mb-3">Competitor suggestions</h3>
                <div className="flex flex-wrap gap-2">
                  {(report.competitors || []).map((c, i) => <Pill key={i} variant="outline">{c}</Pill>)}
                </div>
              </PremiumCard>
            </div>
          </motion.div>
        )}
      </div>
      <PremiumFooter />
    </div>
  );
}
